import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';

export interface SupportTicket {
  id: string;
  customer_id: string;
  workspace_id?: string;
  workspace_name?: string;
  subject: string;
  description: string;
  status: 'Open' | 'In Progress' | 'Closed';
  priority: 'Low' | 'Medium' | 'High';
  assigned_to?: string;
  created_at: Date;
  updated_at: Date;
}

export const getSupportTickets = async (): Promise<{ tickets: SupportTicket[] }> => {
  console.log('Fetching support tickets...');
  try {
    const ticketsRef = collection(db, 'support_tickets');
    const q = query(ticketsRef, orderBy('created_at', 'desc'));
    const querySnapshot = await getDocs(q);
    const tickets = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      created_at: doc.data().created_at?.toDate(),
      updated_at: doc.data().updated_at?.toDate(),
    })) as SupportTicket[];
    console.log('Fetched support tickets:', tickets);
    return { tickets };
  } catch (error) {
    console.error('Error fetching support tickets:', error);
    throw error;
  }
};

export const getSupportTicket = async (id: string): Promise<SupportTicket> => {
  console.log('Fetching support ticket:', id);
  try {
    const ticketRef = doc(db, 'support_tickets', id);
    const ticketDoc = await getDoc(ticketRef);
    if (!ticketDoc.exists()) {
      throw new Error('Support ticket not found');
    }
    const ticket = {
      id: ticketDoc.id,
      ...ticketDoc.data(),
      created_at: ticketDoc.data().created_at?.toDate(),
      updated_at: ticketDoc.data().updated_at?.toDate(),
    } as SupportTicket;
    console.log('Fetched support ticket:', ticket);
    return ticket;
  } catch (error) {
    console.error('Error fetching support ticket:', error);
    throw error;
  }
};

export const addSupportTicket = async (ticket: Omit<SupportTicket, 'id'>): Promise<SupportTicket> => {
  console.log('Adding support ticket:', ticket);
  try {
    const ticketsRef = collection(db, 'support_tickets');
    const newTicketRef = doc(ticketsRef);
    const newTicket = {
      ...ticket,
      id: newTicketRef.id,
      created_at: new Date(),
      updated_at: new Date(),
    };
    await setDoc(newTicketRef, newTicket);
    console.log('Added support ticket:', newTicket);
    return newTicket;
  } catch (error) {
    console.error('Error adding support ticket:', error);
    throw error;
  }
};

export const updateSupportTicket = async (id: string, ticket: Partial<SupportTicket>): Promise<SupportTicket> => {
  console.log('Updating support ticket:', { id, ticket });
  try {
    const ticketRef = doc(db, 'support_tickets', id);
    const updatedTicket = {
      ...ticket,
      updated_at: new Date(),
    };
    await setDoc(ticketRef, updatedTicket, { merge: true });
    const savedTicket = await getSupportTicket(id);
    console.log('Updated support ticket:', savedTicket);
    return savedTicket;
  } catch (error) {
    console.error('Error updating support ticket:', error);
    throw error;
  }
};

export const deleteSupportTicket = async (id: string): Promise<void> => {
  console.log('Deleting support ticket:', id);
  try {
    const ticketRef = doc(db, 'support_tickets', id);
    await deleteDoc(ticketRef);
    console.log('Deleted support ticket:', id);
  } catch (error) {
    console.error('Error deleting support ticket:', error);
    throw error;
  }
}; 